import React from 'react';
import classnames from 'classnames';

import {type AriaTabPanelProps, useTab, useTabList, useTabPanel} from '@react-aria/tabs';
import {useTabListState} from '@react-stately/tabs';
import type {CollectionChildren, Node} from '@react-types/shared';
import type {TabListState, TabListProps} from '@react-stately/tabs';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {ShowMore} from './show_more';

import styles from './styles.module.css';

const DEFAULT_MAX_CHIPS_VISIBLE = 6;

export interface ChipProps {
    allowTabNavigation?: boolean;
    item: Node<Record<string, string>>;
    dot?: boolean;
    state: TabListState<Record<string, string>>;
    className?: string;
}

const Chip: React.FC<ChipProps> = ({item, state, dot, className, allowTabNavigation}) => {
    const {key, rendered} = item;
    const ref = React.useRef(null);
    const {tabProps, isSelected} = useTab({key}, state, ref);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!allowTabNavigation) {
            return;
        }

        if (event.key === 'Tab') {
            const isShiftHeld = event.shiftKey;
            const nextTabKey = isShiftHeld ? state.collection.getKeyBefore(key) : state.collection.getKeyAfter(key);

            if (nextTabKey) {
                event.stopPropagation();
                event.preventDefault();
                state.selectionManager.setFocusedKey(nextTabKey);
            }
        }
    };

    const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
        tabProps.onFocus?.(event);

        if (allowTabNavigation) {
            state.setSelectedKey(key);
        }
    };

    return (
        <div
            {...tabProps}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            tabIndex={allowTabNavigation ? 0 : tabProps.tabIndex}
            ref={ref}
            className={classnames(className, {[styles.active]: isSelected})}
        >
            <div className={styles.dotContainer}>
                {rendered}
                {dot && !isSelected && <div className={styles.dot} />}
            </div>
        </div>
    );
};

export type TabPanelProps = {
    state: TabListState<Record<string, string>>;
    className?: string;
} & AriaTabPanelProps;

const ChipPanel = ({state, className, ...props}: TabPanelProps) => {
    const ref = React.useRef(null);
    const {tabPanelProps} = useTabPanel(props, state, ref);

    return (
        <div
            data-test-id='searchResults'
            {...tabPanelProps}
            tabIndex={-1}
            className={className}
            ref={ref}
        >
            {state.selectedItem?.props.children}
        </div>
    );
};

type ChipGroupVariant = 'flat' | 'neutral';

export type ChipGroupProps = {
    allowTabNavigation?: boolean;
    children: CollectionChildren<Record<string, string>>;
    dots?: React.Key[];
    maxChipsVisible?: number;
    moreLabel?: string;
    className?: string;
    chipPanelClassName?: string;
    variant?: ChipGroupVariant;
} & TabListProps<Record<string, string>>;

const ChipGroup: React.FC<ChipGroupProps> = ({
    allowTabNavigation,
    maxChipsVisible = DEFAULT_MAX_CHIPS_VISIBLE,
    moreLabel,
    dots,
    className,
    chipPanelClassName,
    variant = 'flat',
    ...props
}) => {
    const tabTypography = useTypography({size: TypographySize.BodyM});
    const state = useTabListState(props);
    const ref = React.useRef(null);
    const {tabListProps} = useTabList(props, state, ref);

    const extraTChipsCount = state.collection.size - maxChipsVisible;

    return (
        <div>
            <div data-test-id='tabs' className={classnames(styles.chipGroup, styles[variant], className)}>
                <div
                    {...tabListProps}
                    className={classnames(styles.chipButtons)}
                    ref={ref}
                >
                    {Array.from(state.collection)
                        .slice(0, maxChipsVisible)
                        .map((item) => (
                            <Chip
                                allowTabNavigation={allowTabNavigation}
                                key={item.key}
                                dot={dots?.includes(item.key)}
                                className={classnames(styles.rowChipButton, tabTypography)}
                                item={item}
                                state={state}
                            />
                        ))}
                </div>
                {extraTChipsCount > 0 && moreLabel && (
                    <ShowMore
                        state={state}
                        moreLabel={moreLabel}
                        count={extraTChipsCount}
                    />
                )}
            </div>
            <ChipPanel
                className={chipPanelClassName}
                key={state.selectedItem?.key}
                state={state}
            />
        </div>
    );
};

export {ChipGroup};
